/**
 * Uploads the offline queue when the register is online again (T07-33 ↔ T07-17):
 * `POST /pos/sync/batch` with ≤ 20 operations at a time, oldest first, every answer handled per op:
 *
 * | answer | on the tablet |
 * |---|---|
 * | `ok` | removed from the queue; the real receipt number (`R01-…`) is kept in `syncResults` (the queue screen shows it; the real receipt can be printed) |
 * | `duplicate` | removed (it was uploaded before — a double tap / a lost answer); never shown twice |
 * | `conflict` | removed; a red 要確認 card (the card was sold elsewhere — the cash is kept, the manager decides) |
 * | `rejected` | removed; a 要確認 card with the reason (the manager records it by hand) |
 * | `pending` | kept (a service did not answer) — sent again later with the same key |
 *
 * One run at a time (a second 「今すぐ同期」 waits for the first); a network failure keeps
 * everything as it was.
 */
import type { PosClient } from "@tcg/api-client";

import { call } from "@/lib/api";
import type { PosDatabase, QueuedOp, SyncResultRecord } from "@/lib/db";
import type { SyncOpResult } from "@/lib/types";

import { queuedOps } from "./opQueue";

export const SYNC_BATCH_SIZE = 20;
export const SYNC_RETRY_MS = 30_000;

export interface SyncRunResult {
  sent: number;
  ok: number;
  duplicate: number;
  conflict: number;
  rejected: number;
  pending: number;
  error: string | null;
}

export interface SyncState {
  running: boolean;
  lastRunAt: number | null;
  last: SyncRunResult | null;
}

const EMPTY: SyncRunResult = {
  sent: 0,
  ok: 0,
  duplicate: 0,
  conflict: 0,
  rejected: 0,
  pending: 0,
  error: null,
};

export class SyncWorker {
  private state: SyncState = { running: false, lastRunAt: null, last: null };
  private readonly listeners = new Set<() => void>();
  private active: Promise<SyncRunResult> | null = null;

  constructor(
    private readonly db: PosDatabase,
    private readonly api: PosClient,
    private readonly deviceId: () => string | null,
    private readonly now: () => number = Date.now,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): SyncState => this.state;

  private set(next: Partial<SyncState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  /** Upload everything queued (in batches). A run already going is joined, not repeated. */
  run(): Promise<SyncRunResult> {
    this.active ??= this.upload().finally(() => {
      this.active = null;
    });
    return this.active;
  }

  private async upload(): Promise<SyncRunResult> {
    const total: SyncRunResult = { ...EMPTY };
    this.set({ running: true });
    try {
      // a batch at a time, oldest first; each op is sent at most once per run (a `pending` one
      // waits for the next run) — a network failure stops the run and keeps the queue as it was
      const sent = new Set<string>();
      for (;;) {
        const due = (await queuedOps(this.db))
          .filter((op) => !sent.has(op.idempotencyKey))
          .slice(0, SYNC_BATCH_SIZE);
        if (!due.length) {
          break;
        }
        for (const op of due) {
          sent.add(op.idempotencyKey);
        }
        await this.batch(due, total);
      }
    } catch (error) {
      total.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ running: false, lastRunAt: this.now(), last: total });
    }
    return total;
  }

  /** One `POST /pos/sync/batch`; true when at least one op left the queue. */
  private async batch(ops: QueuedOp[], total: SyncRunResult): Promise<boolean> {
    const deviceId = this.deviceId();
    const answer = await call(() =>
      this.api.POST("/v1/pos/sync/batch", {
        body: {
          ...(deviceId ? { deviceId } : {}),
          operations: ops.map((op) => ({
            idempotencyKey: op.idempotencyKey,
            type: op.kind,
            clientTime: op.clientTime ?? new Date(op.createdAt).toISOString(),
            payload: (op.payload ?? {}) as Record<string, unknown>,
          })),
        },
        // every op carries its own idempotencyKey (the server answers `duplicate` for a replay);
        // the request's own Idempotency-Key is the API layer's usual fresh one
      }),
    );
    total.sent += ops.length;
    const byKey = new Map(ops.map((op) => [op.idempotencyKey, op]));
    let moved = false;
    await this.db.transaction("rw", this.db.opQueue, this.db.syncResults, async () => {
      for (const result of answer.results) {
        const op = byKey.get(result.idempotencyKey);
        if (!op) {
          continue;
        }
        if (result.status === "pending") {
          total.pending += 1;
          await this.db.opQueue.update(op.id ?? 0, {
            attempts: op.attempts + 1,
            lastError: result.error?.code ?? "pending",
          });
          continue;
        }
        moved = true;
        total[result.status] += 1;
        await this.db.opQueue.delete(op.id ?? 0);
        await this.record(op, result);
      }
    });
    return moved;
  }

  private async record(op: QueuedOp, result: SyncOpResult): Promise<void> {
    const status = result.status as SyncResultRecord["status"];
    const previous = await this.db.syncResults.get(op.idempotencyKey);
    if (status === "duplicate" && previous) {
      return; // already shown once — never a second bill on the screen
    }
    const review = status === "conflict" || status === "rejected" || result.needsReview;
    await this.db.syncResults.put({
      idempotencyKey: op.idempotencyKey,
      kind: op.kind,
      status,
      needsReview: review && op.kind === "sale_cash" ? 1 : 0,
      saleId: result.saleId ?? null,
      receiptNo: result.receiptNo ?? null,
      orderNumber: result.orderNumber ?? null,
      tempReceiptNo: result.tempReceiptNo ?? op.summary?.tempReceiptNo ?? null,
      total: result.total ?? op.summary?.total ?? null,
      reasons: result.reviewReasons,
      errorCode: result.error?.code ?? null,
      errorMessage: result.error?.message ?? null,
      ...(op.summary ? { summary: op.summary } : {}),
      ...(op.clientTime ? { clientTime: op.clientTime } : {}),
      syncedAt: this.now(),
      resolvedAt: null,
      resolvedBy: null,
    });
  }
}

/** 要確認 cards not closed by a manager yet. */
export function openReviews(db: PosDatabase): Promise<SyncResultRecord[]> {
  return db.syncResults
    .where("needsReview")
    .equals(1)
    .filter((r) => !r.resolvedAt)
    .toArray();
}
